import { OverlayLifecycle } from './overlay-lifecycle.js';
import { RecentlyViewedController } from 'folded-label/recently-viewed';
const type = 'product';

export function nativeSearchUrl(searchUrl, query, baseUrl = 'https://storefront.invalid') {
  const url = new URL(searchUrl, baseUrl);
  url.searchParams.set('q', query);
  url.searchParams.set('type', type);
  return `${url.pathname}${url.search}`;
}

export function predictiveSearchUrl(predictiveUrl, query, baseUrl = 'https://storefront.invalid') {
  const url = new URL(predictiveUrl, baseUrl);
  url.searchParams.set('q', query);
  url.searchParams.set('resources[type]', 'product,collection,query');
  url.searchParams.set('resources[limit]', '8');
  url.searchParams.set('resources[options][unavailable_products]', 'hide');
  url.searchParams.set('section_id', 'fl-predictive-search-results');
  return `${url.pathname}${url.search}`;
}

export function responseIsCurrent(sequence, expectedSequence, currentQuery, responseQuery) {
  return sequence === expectedSequence && currentQuery === responseQuery;
}

const ElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class PredictiveSearch extends ElementBase {
  connectedCallback() {
    this.abortController?.abort();
    this.controller?.abort();
    this.controller = new AbortController();
    this.dialog = this.querySelector('[data-search-dialog]');
    this.form = this.querySelector('[data-search-form]');
    this.input = this.querySelector('[data-search-input]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.closeButton = this.querySelector('[data-search-close]');
    this.initial = this.querySelector('[data-search-initial]');
    this.dynamic = this.querySelector('[data-search-dynamic]');
    this.sequence = 0;
    this.timer = null;
    this.overlay = new OverlayLifecycle(this.dialog, {
      focus: () => this.input,
      beforeClose: () => {
        this.cancelSearch();
        this.recentlyViewed?.surfaceHidden();
      },
      afterClose: () => this.reset({ hydrateRecent: false }),
    });
    if (this.dataset.recentlyViewedEnabled === 'true') {
      this.recentlyViewed = new RecentlyViewedController(this);
      this.recentlyViewed.connect();
    }

    document.querySelectorAll('[data-open-search]').forEach(opener => opener.addEventListener('click', event => {
      if (!this.canOpen()) return;
      event.preventDefault();
      this.open(opener);
    }, { signal: this.controller.signal }));
    this.input?.addEventListener('input', () => this.schedule(), { signal: this.controller.signal });
    this.form?.addEventListener('submit', event => {
      if (!this.input) return;
      event.preventDefault();
      window.location.assign(nativeSearchUrl(this.dataset.searchUrl || '/search', this.input.value.trim(), window.location.href));
    }, { signal: this.controller.signal });
    this.clearButton?.addEventListener('click', () => this.reset({ focusInput: true }), { signal: this.controller.signal });
    this.closeButton?.addEventListener('click', () => this.overlay.close(), { signal: this.controller.signal });
    this.dialog?.addEventListener('keydown', event => {
      // A filled native search input otherwise consumes Escape to clear itself
      // before the dialog receives its cancel event.
      if (event.key === 'Escape') { event.preventDefault(); this.overlay.close(); }
      else this.trapFocus(event);
    }, { signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.cancelSearch();
    this.recentlyViewed?.disconnect();
    this.overlay?.dispose();
    this.controller?.abort();
  }

  canOpen() {
    return Boolean(this.dialog && typeof this.dialog.showModal === 'function');
  }

  async open(opener) {
    const opened = await this.overlay.open(opener);
    // An interrupted exit canceled predictive work without reaching afterClose.
    // A successful reopen starts from usable suggestions; duplicate opens do not reset.
    if (opened) {
      this.reset({ hydrateRecent: false });
      this.recentlyViewed?.surfaceOpened();
    }
    return opened;
  }

  cancelSearch() {
    this.sequence += 1;
    this.abortController?.abort();
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = null;
  }

  trapFocus(event) {
    if (event.key !== 'Tab' || !this.dialog) return;
    const focusable = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter(element => !element.hidden && element.getClientRects().length);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  }

  schedule() {
    if (!this.input) return;
    if (this.timer) window.clearTimeout(this.timer);
    const query = this.input.value.trim();
    this.clearButton.hidden = !query;
    if (!query) {
      this.reset();
      return;
    }
    this.recentlyViewed?.queryStarted();
    this.timer = window.setTimeout(() => this.search(query), 180);
  }

  reset({ focusInput = false, hydrateRecent = this.dialog?.open === true } = {}) {
    if (!this.input || !this.initial || !this.dynamic) return;
    this.sequence += 1;
    this.abortController?.abort();
    if (this.timer) window.clearTimeout(this.timer);
    this.input.value = '';
    this.clearButton.hidden = true;
    this.initial.hidden = false;
    this.dynamic.hidden = true;
    this.dynamic.replaceChildren();
    this.dynamic.setAttribute('aria-busy', 'false');
    if (hydrateRecent) this.recentlyViewed?.queryCleared();
    if (focusInput) this.input.focus();
  }

  async search(query) {
    if (!this.initial || !this.dynamic) return;
    const expectedSequence = ++this.sequence;
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.initial.hidden = true;
    this.dynamic.hidden = false;
    this.dynamic.setAttribute('aria-busy', 'true');
    this.dynamic.innerHTML = '<p class="search-overlay-loading" role="status">Searching pieces…</p>';
    try {
      const url = predictiveSearchUrl(this.dataset.predictiveUrl || '/search/suggest', query, window.location.href);
      const response = await fetch(url, { signal: this.abortController.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);
      const documentFragment = new window.DOMParser().parseFromString(await response.text(), 'text/html');
      const fragment = documentFragment.querySelector('[data-predictive-fragment]');
      if (!fragment) throw new Error('Predictive search returned an invalid section.');
      if (!responseIsCurrent(this.sequence, expectedSequence, this.input?.value.trim() || '', query)) return;
      this.dynamic.replaceChildren(...fragment.childNodes);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      if (!responseIsCurrent(this.sequence, expectedSequence, this.input?.value.trim() || '', query)) return;
      const searchUrl = nativeSearchUrl(this.dataset.searchUrl || '/search', query, window.location.href);
      this.dynamic.innerHTML = `<div class="predictive-search-error" role="alert"><h2>Search is temporarily unavailable</h2><p>Please try again, or use the full search page.</p><a href="${searchUrl}">View all results for this search</a></div>`;
    } finally {
      if (responseIsCurrent(this.sequence, expectedSequence, this.input?.value.trim() || '', query)) this.dynamic.setAttribute('aria-busy', 'false');
    }
  }
}

if (typeof window !== 'undefined' && !customElements.get('fl-predictive-search')) customElements.define('fl-predictive-search', PredictiveSearch);
